import React, { useState, useEffect } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Container,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Chip
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Notifications as NotificationsIcon,
  Chat as ChatIcon,
  AccessTime as AccessTimeIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import io from 'socket.io-client';

const API_BASE_URL = 'https://agent.1lb6ey6g0jkt.iconaccounting.ie';

const WaitingChats = () => {
  const [waitingChats, setWaitingChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedChat, setSelectedChat] = useState(null);
  const [showPickChatConfirm, setShowPickChatConfirm] = useState(false);
  const [audioContext, setAudioContext] = useState(null);
  const titleFlashIntervalRef = useState(null)[0];
  const originalTitleRef = useState(document.title)[0];
  const [socket, setSocket] = useState(null);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  // Check authentication and get user info
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('authToken');
      if (!token) {
        navigate('/login');
        return;
      }

      try {
        const response = await axios.get(`${API_BASE_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(response.data);
        console.log('👤 User authenticated:', response.data.username);
      } catch (error) {
        console.error('Authentication failed:', error);
        localStorage.removeItem('authToken');
        navigate('/login');
      }
    };

    checkAuth();
  }, [navigate]);

  // Initialize audio context for notification sounds
  useEffect(() => {
    const initAudio = () => {
      try {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        const context = new AudioContext();
        setAudioContext(context);
        console.log('🔊 Audio context initialized successfully');
      } catch (error) {
        console.error('Audio context not supported:', error);
      }
    };

    initAudio();
  }, []);

  // Connect to socket for real-time notifications
  useEffect(() => {
    connectSocket();
    return () => {
      if (socket) {
        socket.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    fetchWaitingChats();
  }, []);

  // Play notification sound with better audio
  const playNotificationSound = () => {
    if (!audioContext) {
      console.log('🔇 Audio context not available');
      return;
    }

    try {
      // Resume audio context if suspended (required by browsers)
      if (audioContext.state === 'suspended') {
        audioContext.resume();
      }

      // Create a more noticeable "ding dong" sound (louder)
      const playTone = (frequency, duration, delay = 0) => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        oscillator.frequency.setValueAtTime(frequency, audioContext.currentTime + delay);
        oscillator.type = 'sine';

        // Better volume envelope
        gainNode.gain.setValueAtTime(0, audioContext.currentTime + delay);
        gainNode.gain.linearRampToValueAtTime(0.7, audioContext.currentTime + delay + 0.05);
        gainNode.gain.exponentialRampToValueAtTime(0.03, audioContext.currentTime + delay + duration);

        oscillator.start(audioContext.currentTime + delay);
        oscillator.stop(audioContext.currentTime + delay + duration);
      };

      // Play "ding dong" sound (two tones)
      playTone(800, 0.3, 0);    // First tone (ding)
      playTone(600, 0.3, 0.4);  // Second tone (dong)

      console.log('🔔 Ding-dong notification sound played');
    } catch (error) {
      console.error('Error playing notification sound:', error);
    }
  };

  // Simple tab flash for new items
  const startTitleFlash = (text = 'New chat activity') => {
    if (!document.hidden) return;
    if (window.__iconbotFlashInterval) return;
    window.__iconbotOriginalTitle = document.title;
    let toggle = false;
    window.__iconbotFlashInterval = setInterval(() => {
      document.title = toggle ? text : window.__iconbotOriginalTitle;
      toggle = !toggle;
    }, 800);
  };

  const stopTitleFlash = () => {
    if (window.__iconbotFlashInterval) {
      clearInterval(window.__iconbotFlashInterval);
      window.__iconbotFlashInterval = null;
      if (window.__iconbotOriginalTitle) document.title = window.__iconbotOriginalTitle;
    }
  };

  // Fallback polling mechanism in case WebSocket fails
  const startPollingFallback = () => {
    console.log('🔄 Starting fallback polling every 5 seconds');
    const interval = setInterval(() => {
      fetchWaitingChats();
    }, 5000);
    
    // Clean up interval when component unmounts
    return () => clearInterval(interval);
  };

  const connectSocket = () => {
    if (socket) {
      socket.disconnect();
    }

    const newSocket = io(API_BASE_URL);
    setSocket(newSocket);

    newSocket.on('connect', () => {
      console.log('🔌 Socket connected');
      const token = localStorage.getItem('authToken');
      if (token) newSocket.emit('join_support', token);
    });

    newSocket.on('new_chat_started', (data) => {
      console.log('🆕 New chat started:', data);
      playNotificationSound();
      startTitleFlash('New waiting chat');
      fetchWaitingChats();
    });

    newSocket.on('new_message', (data) => {
      console.log('💬 New message received:', data);
      playNotificationSound();
      startTitleFlash('New customer message');
      fetchWaitingChats();
    });

    newSocket.on('chat_ended', (data) => {
      console.log('🔚 Chat ended:', data);
      console.log('🔄 Refreshing waiting chats due to chat ended event...');
      fetchWaitingChats();
    });

    newSocket.on('chat_finished', (data) => {
      console.log('✅ Chat finished:', data);
      fetchWaitingChats();
    });

    newSocket.on('chat_picked_up', (data) => {
      console.log('👨‍💼 Chat picked up:', data);
      fetchWaitingChats();
    });

    newSocket.on('user_info_updated', (data) => {
      console.log('👤 User info updated:', data);
      fetchWaitingChats();
    });

    newSocket.on('disconnect', () => {
      console.log('🔌 Socket disconnected');
    });

    newSocket.on('connect_error', (error) => {
      console.error('🔌 Socket connection error:', error);
      // Fallback to polling if socket fails
      setTimeout(() => {
        console.log('🔄 Falling back to polling due to socket error');
        startPollingFallback();
      }, 5000);
    });
  };

  useEffect(() => {
    const handleVisibility = () => {
      if (!document.hidden) stopTitleFlash();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  const fetchWaitingChats = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_BASE_URL}/api/support/waiting`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWaitingChats(response.data);
    } catch (error) {
      console.error('Failed to fetch waiting chats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePickChat = (chat) => {
    setSelectedChat(chat);
    setShowPickChatConfirm(true);
  };

  const confirmPickChat = async () => {
    if (!selectedChat) return;

    try {
      const token = localStorage.getItem('authToken');
      await axios.put(`${API_BASE_URL}/api/support/pick-chat/${selectedChat.session_id}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('✅ Chat picked up successfully');
      setShowPickChatConfirm(false);
      setSelectedChat(null);
      
      // Navigate to active chats
      navigate('/chats');
    } catch (error) {
      console.error('❌ Failed to pick up chat:', error);
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const getWaitingTime = (timestamp) => {
    const now = new Date();
    const created = new Date(timestamp);
    const diffMs = now - created;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  return (
    <Box>
      <AppBar 
        position="fixed"
        sx={{
          background: 'linear-gradient(135deg, #00aae5 0%, #0099cc 100%)',
          boxShadow: '0 4px 20px rgba(0, 170, 229, 0.3)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
        }}
      >
        <Toolbar sx={{ minHeight: '70px' }}>
          <IconButton
            color="inherit"
            onClick={() => navigate('/dashboard')}
            sx={{ 
              mr: 2,
              borderRadius: '50%',
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                transform: 'scale(1.05)'
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography 
            variant="h6" 
            component="div" 
            sx={{ 
              flexGrow: 1,
              fontWeight: 600,
              fontSize: '1.25rem'
            }}
          >
            Waiting Chats ({waitingChats.length})
          </Typography>
          
          {/* User Info */}
          {user && (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2, 
              mr: 2,
              p: 1,
              borderRadius: '8px',
              bgcolor: 'rgba(255, 255, 255, 0.1)'
            }}>
              <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
                {user.username}
              </Typography>
            </Box>
          )}
          <IconButton
            color="inherit"
            onClick={fetchWaitingChats}
            disabled={loading}
            sx={{ 
              borderRadius: '50%',
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                transform: 'scale(1.05)'
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : <NotificationsIcon />}
          </IconButton>
          <IconButton
            color="inherit"
            onClick={playNotificationSound}
            sx={{ 
              borderRadius: '50%',
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              color: 'white',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                transform: 'scale(1.05)'
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
            title="Test Sound"
          >
            🔊
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 8, p: 3 }}>
        <Grid container spacing={3}>
          {waitingChats.map((chat) => (
            <Grid item xs={12} md={6} lg={4} key={chat.session_id}>
              <Card sx={{ 
                borderRadius: '20px',
                background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)'
                }
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <ChatIcon sx={{ 
                      color: '#667eea', 
                      mr: 1,
                      fontSize: 24
                    }} />
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 600,
                        color: '#2d3748'
                      }}
                    >
                      {chat.user_name || 'Anonymous Customer'}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ mb: 2 }}>
                    <Chip
                      icon={<AccessTimeIcon />}
                      label={getWaitingTime(chat.created_at)}
                      sx={{
                        background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
                        color: 'white',
                        fontWeight: 500,
                        fontSize: '0.875rem'
                      }}
                    />
                  </Box>

                  <Box sx={{ mb: 3 }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#64748b',
                        mb: 1
                      }}
                    >
                      <strong>Messages:</strong> {chat.message_count}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#64748b'
                      }}
                    >
                      <strong>Started:</strong> {formatTime(chat.created_at)}
                    </Typography>
                  </Box>

                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => handlePickChat(chat)}
                                         sx={{
                       background: 'linear-gradient(135deg, #00aae5 0%, #0099cc 100%)',
                       borderRadius: '12px',
                       py: 1.5,
                       textTransform: 'none',
                       fontWeight: 600,
                       fontSize: '1rem',
                       boxShadow: '0 4px 12px rgba(0, 170, 229, 0.3)',
                       '&:hover': {
                         background: 'linear-gradient(135deg, #0099cc 0%, #0088b3 100%)',
                         transform: 'scale(1.02)',
                         boxShadow: '0 6px 16px rgba(0, 170, 229, 0.4)'
                       },
                       transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                     }}
                  >
                    Pick Up Chat
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {waitingChats.length === 0 && !loading && (
          <Box sx={{ 
            textAlign: 'center', 
            mt: 8,
            p: 4
          }}>
            <NotificationsIcon sx={{ 
              fontSize: 64, 
              color: '#cbd5e0',
              mb: 2
            }} />
            <Typography 
              variant="h5" 
              sx={{ 
                color: '#64748b',
                fontWeight: 500,
                mb: 1
              }}
            >
              No Waiting Chats
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: '#94a3b8'
              }}
            >
              All customers are being helped at the moment
            </Typography>
          </Box>
        )}
      </Container>

      {/* Pick Chat Confirmation Dialog */}
      <Dialog
        open={showPickChatConfirm}
        onClose={() => setShowPickChatConfirm(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)'
          }
        }}
      >
        <DialogTitle          sx={{ 
           background: 'linear-gradient(135deg, #00aae5 0%, #0099cc 100%)',
           color: 'white',
           borderRadius: '20px 20px 0 0',
           fontWeight: 600
         }}>
          Pick Up Chat
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Typography sx={{ fontSize: '1rem', lineHeight: 1.6 }}>
            Are you sure you want to pick up this chat with{' '}
            <strong>{selectedChat?.user_name || 'Anonymous Customer'}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button 
            onClick={() => setShowPickChatConfirm(false)}
            sx={{
              borderRadius: '12px',
              px: 3,
              py: 1,
              textTransform: 'none',
              fontWeight: 500
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmPickChat}
                         sx={{
               borderRadius: '12px',
               px: 3,
               py: 1,
               background: 'linear-gradient(135deg, #00aae5 0%, #0099cc 100%)',
               color: 'white',
               textTransform: 'none',
               fontWeight: 500,
               '&:hover': {
                 background: 'linear-gradient(135deg, #0099cc 0%, #0088b3 100%)'
               }
             }}
          >
            Pick Up
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WaitingChats;
